import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Login } from "./components/Auth/Login";
import { Navigate, Route, Routes } from 'react-router-dom';
import { Home } from "./components/Home/Home";
import "./assets/css/bootstrap.min.css";
import "./index.css";
import "./theme.css";
import './App.css';
import { LayoutShell } from "./components/LayoutShell/LayoutShell";
import { Register } from "./components/Auth/Register";
import { Dashboard } from "./components/Dashboard/Dashboard";
import { AdminDashboard } from "./components/AdminDashboard/AdminDashboard";
import { useContextStateManagement } from "./context/ContextApi";
import { useLocation } from "react-router-dom";
import { DeviceNameStep } from "./components/Auth/DeviceNameStep";
import { QRCodeAuth } from "./components/Auth/QRCodeAuth";
import VerifyMFA from "./components/Auth/VerifyMFA";
import EmailNotificationUser from "./components/Auth/EmailNotificationUser";
import { VoiceOpsPage } from "./components/VoiceOpsPage/VoiceOpsPage";
import Homepage from "./components/Homepage/Homepage";
import { AgentConfigPage } from "./components/AgentConfigPage.js";
import { ToolDefinitionsPage } from "./components/ToolDefinitionsPage.js";
import { AgentReleasesPage } from "./components/AgentReleasesPage.js";
import { NotFoundPage } from "./components/NotFound/NotFoundPage";

const isVoiceOpsEnabled = process.env.REACT_APP_VOICE_OPS_ENABLED !== "false";

const RouteLoading = () => (
  <div className="d-flex justify-content-center mt-5 pt-5">
    <div className="spinner-border text-light" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  const user = useSelector(state => state.user.user);
  const location = useLocation();
  const {
    hasPageAccess,
    setDashboardUrl,
    setSideBarLinkActive,
    userProfession,
    userName,
    userType,
    PracticeId,
    token,
  } = useContextStateManagement();

  // Keeps PreferredTimezone in sync with the browser's own detected zone on every login, as
  // long as the user hasn't opted out of that (TimezoneSyncMode === "fixed", set via the
  // Settings timezone toggle when they deliberately want to keep a specific zone - e.g. to
  // match a teammate's reports - instead of always tracking whichever device they're on).
  // "auto" (the default for everyone) means this re-detects and re-saves every login, so it
  // always reflects wherever they actually are right now.
  //
  // TIMEZONE_SYNC_SESSION_KEY guards this to run once per real login instead of on every
  // [user, token] change - Home.jsx's timezone dropdown lets an "auto" user preview a different
  // zone for the rest of this session (Settings copy: "previews it for this session only, next
  // login resets to local time"), by PATCHing PreferredTimezone directly without touching
  // TimezoneSyncMode. A plain useRef guard isn't enough here - it resets on every page refresh
  // (a fresh mount of this same component, still the same login/token), which re-ran this effect
  // and PATCHed the just-previewed zone straight back to local the moment the user reloaded,
  // not just at their next actual login. sessionStorage survives a refresh but clears when the
  // browser tab itself closes, which is a much closer match to "this session" than a component
  // remount is.
  const TIMEZONE_SYNC_SESSION_KEY = "cm_timezoneSyncedThisSession";

  // Clears the guard the instant this user actually logs out, so a subsequent login in the
  // same browser tab (no refresh/tab-close involved) still gets treated as "next login" and
  // re-syncs, instead of only ever re-syncing once per tab lifetime.
  const wasLoggedInRef = useRef(false);
  useEffect(() => {
    if (!user && wasLoggedInRef.current) {
      try {
        sessionStorage.removeItem(TIMEZONE_SYNC_SESSION_KEY);
      } catch (error) {
        // Non-critical.
      }
    }
    wasLoggedInRef.current = Boolean(user);
  }, [user]);

  useEffect(() => {
    if (!user || !token) return undefined;
    let hasSynced = false;
    try {
      hasSynced = sessionStorage.getItem(TIMEZONE_SYNC_SESSION_KEY) === "true";
    } catch (error) {
      // sessionStorage unavailable (private browsing, etc.) - fall back to syncing every mount,
      // same as before this fix existed.
    }
    if (hasSynced) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const response = await axios.get("/api/getPreferredTimezone", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        try {
          sessionStorage.setItem(TIMEZONE_SYNC_SESSION_KEY, "true");
        } catch (error) {
          // Non-critical - worst case this re-syncs again on the next refresh too.
        }

        const syncMode = response.data?.TimezoneSyncMode || "auto";
        if (syncMode === "fixed") return;

        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!detected || cancelled || detected === response.data?.PreferredTimezone) return;

        await axios.patch(
          "/api/updatePreferredTimezone",
          { PreferredTimezone: detected },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (error) {
        // Non-critical - worst case this user keeps seeing a stale/default timezone until
        // this succeeds on a later load.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, token]);
  const [isUploadLead, setIsUploadLead] = useState(false);
  const [isTabClick, setIsTabClick] = useState(true);
  const [deviceName, setDeviceName] = useState("");
  const [currentRoute, setCurrentRoute] = useState("login");
  const [userDataAccess, setUserDataAccess] = useState([]);
  const [registeredUserEmail, setRegisteredUserEmail] = useState("");
  const [registerdUserToken, setRegisterdUserToken] = useState("");
  const [isDeviceName, setIsDeviceName] = useState(false);
  const [outBoundLeadsDetail, setOutBoundLeadsDetail] = useState({});
  // Summary deep-link detection (only for ?tab=summary)
  const hasSummaryTab = new URLSearchParams(location.search).get("tab") === "summary";
  const summaryQuery = hasSummaryTab ? location.search : "";
  const isPWADialer = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const noPageAccessElement = (
    <div className="p-4 text-light">
      No pages have been assigned to this organization yet.
    </div>
  );

  const clientRedirectPath =
    [
      ["careConsole", "/", true],
      ["dashboard", "/Dashboard", true],
      ["support", "/support", true],
      ["dialer", "/dialer", true],
      ["onBoardingForm", "/prompt-hub", false],
      ["agentConfig", "/agent-config", true],
      ["apiIntegration", "/api-integration", false],
      ["toolDefinitions", "/tools", true],
      ["agentReleases", "/agent-releases", true],
      ["voiceOps", "/VoiceOps", false],
      ["tickets", "/tickets", false],
    ].find(([pageId, _path, fallbackVisible]) =>
      hasPageAccess(pageId, fallbackVisible),
    )?.[1] || "";

  const renderOrgProtectedRoute = (pageId, fallbackVisible, element) => {
    if (userType === "admin" || hasPageAccess(pageId, fallbackVisible)) {
      return element;
    }

    if (clientRedirectPath) {
      return <Navigate to={clientRedirectPath} replace />;
    }

    return noPageAccessElement;
  };

  // useEffect(() => {
  //   window.history.pushState(currentUrl, document.title, window.location.href);
  //   window.history.pushState(dashboardUrl, document.title, window.location.href);
  //   window.addEventListener('popstate', function (event) {
  //     window.history.pushState(currentUrl, document.title, window.location.href);
  //     window.history.pushState(dashboardUrl, document.title, window.location.href);
  //   });
  // }, [location, currentUrl]);

  useEffect(() => {
    const isHomeOrDashboard =
      location.pathname === "/" || location.pathname === "/Dashboard";

    if (!isHomeOrDashboard) return;

    // Prevent browser back to login/signup/MFA
    window.history.pushState(null, document.title, window.location.href);
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      window.history.pushState(null, document.title, window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (location.pathname === "/prompt-hub") {
      setSideBarLinkActive("onBoardingForm");
      return;
    }

    if (location.pathname === "/agent-config") {
      setSideBarLinkActive("agentConfig");
      return;
    }

    if (location.pathname === "/api-integration") {
      setSideBarLinkActive("apiIntegration");
      return;
    }

    if (location.pathname === "/tools") {
      setSideBarLinkActive("toolDefinitions");
      return;
    }

    if (location.pathname === "/agent-releases") {
      setSideBarLinkActive("agentReleases");
      return;
    }

    if (location.pathname === "/add-users") {
      setSideBarLinkActive("addUsers");
      return;
    }

    if (location.pathname === "/admin-center") {
      setSideBarLinkActive("manageOrg");
      return;
    }

    if (location.pathname === "/support") {
      setSideBarLinkActive("support");
      return;
    }

    if (location.pathname === "/homepage") {
      setSideBarLinkActive("homepage");
      return;
    }

    if (location.pathname === "/tickets") {
      setSideBarLinkActive("tickets");
      return;
    }

    if (location.pathname === "/dialer") {
      setSideBarLinkActive("dialer");
      return;
    }

    if (location.pathname === "/Dashboard") {
      setSideBarLinkActive("dashboard");
      return;
    }

    if (location.pathname === "/AdminDashboard") {
      setSideBarLinkActive("adminDashboard");
      return;
    }

    if (location.pathname.startsWith("/VoiceOps")) {
      setSideBarLinkActive("voiceOps");
    }
  }, [location.pathname, setSideBarLinkActive, user]);

  const homeElement = (
    <Home
      outBoundLeadsDetail={outBoundLeadsDetail}
      setOutBoundLeadsDetail={setOutBoundLeadsDetail}
      setUserDataAccess={setUserDataAccess}
      setCurrentRoute={setCurrentRoute}
      setIsUploadLead={setIsUploadLead}
      isUploadLead={isUploadLead}
      setIsTabClick={setIsTabClick}
    />
  );


  return (
    <>
      <Routes>
        <Route
          path="/signup"
          element={
            user ? (
              <Navigate to={location.state?.from || "/"} replace />
            ) : (
              <Register
                setRegisterdUserToken={setRegisterdUserToken}
                setCurrentRoute={setCurrentRoute}
                registeredUserEmail={registeredUserEmail}
                setRegisteredUserEmail={setRegisteredUserEmail}
              />
            )
          }
        />
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to={location.state?.from || "/"} replace />
            ) : (
              <Login
                setIsTabClick={setIsTabClick}
                setRegisterdUserToken={setRegisterdUserToken}
                setRegisteredUserEmail={setRegisteredUserEmail}
                setUserDataAccess={setUserDataAccess}
                userDataAccess={userDataAccess}
                setCurrentRoute={setCurrentRoute}
              />
            )
          }
        />
        {/* Support Summary deep link login path while preserving query */}
        <Route
          path="/login/VerifM"
          element={
            user ? (
              <Navigate to={location.state?.from || "/"} replace />
            ) : (
              <Login
                setIsTabClick={setIsTabClick}
                setRegisterdUserToken={setRegisterdUserToken}
                setRegisteredUserEmail={setRegisteredUserEmail}
                setUserDataAccess={setUserDataAccess}
                userDataAccess={userDataAccess}
                setCurrentRoute={setCurrentRoute}
              />
            )
          }
        />

        {!user && currentRoute === "DeviceName" && (
          <Route
            path="*"
            element={
              <DeviceNameStep
                setIsTabClick={setIsTabClick}
                setIsDeviceName={setIsDeviceName}
                isDeviceName={isDeviceName}
                setCurrentRoute={setCurrentRoute}
                setDeviceName={setDeviceName}
                deviceName={deviceName}
              />
            }
          />
        )}
        {!user && currentRoute === "QRCodeAuthentication" && (
          <Route
            path="*"
            element={
              <QRCodeAuth
                userDataAccess={userDataAccess}
                setIsDeviceName={setIsDeviceName}
                isDeviceName={isDeviceName}
                setCurrentRoute={setCurrentRoute}
                currentRoute={currentRoute}
                setRegisterdUserToken={setRegisterdUserToken}
                setRegisteredUserEmail={setRegisteredUserEmail}
                registeredUserEmail={registeredUserEmail}
                registerdUserToken={registerdUserToken}
                setDeviceName={setDeviceName}
                deviceName={deviceName}
              />
            }
          />
        )}
        {!user && currentRoute === "VerifyMfa" && (
          <Route
            path="*"
            element={
              <VerifyMFA
                outBoundLeadsDetail={outBoundLeadsDetail}
                setOutBoundLeadsDetail={setOutBoundLeadsDetail}
                setCurrentRoute={setCurrentRoute}
                setDeviceName={setDeviceName}
                setUserDataAccess={setUserDataAccess}
                userDataAccess={userDataAccess}
                setRegisterdUserToken={setRegisterdUserToken}
                setRegisteredUserEmail={setRegisteredUserEmail}
                registerdUserToken={registerdUserToken}
                registeredUserEmail={registeredUserEmail}
              />
            }
          />
        )}
        {!user && currentRoute === "UserEmailNotification" && (
          <Route
            path="*"
            element={<EmailNotificationUser setCurrentRoute={setCurrentRoute} />}
          />
        )}

        <Route
          path="/*"
          element={
            user ? (
              <LayoutShell
                isPWADialer={isPWADialer}
                outBoundLeadsDetail={outBoundLeadsDetail}
                setOutBoundLeadsDetail={setOutBoundLeadsDetail}
                setUserDataAccess={setUserDataAccess}
                setCurrentRoute={setCurrentRoute}
                setIsUploadLead={setIsUploadLead}
                setIsTabClick={setIsTabClick}
                isTabClick={isTabClick}
              >
                <Routes>
                  <Route
                    path="/"
                    element={renderOrgProtectedRoute("careConsole", true, homeElement)}
                  />
                  <Route
                    path="/support"
                    element={renderOrgProtectedRoute("support", true, homeElement)}
                  />
                  <Route
                    path="/tickets"
                    element={renderOrgProtectedRoute("tickets", false, homeElement)}
                  />
                  <Route
                    path="/dialer"
                    element={renderOrgProtectedRoute("dialer", true, homeElement)}
                  />
                  <Route
                    path="/api-integration"
                    element={renderOrgProtectedRoute("apiIntegration", false, homeElement)}
                  />
                  <Route
                    path="/prompt-hub/:workflow"
                    element={renderOrgProtectedRoute("onBoardingForm", false, homeElement)}
                  />
                  {[
                    "/inbound",
                    "/outbound",
                    "/summary",
                    "/call-settings",
                    "/bulk-upload",
                    "/add-users",
                    "/admin-center",
                  ].map((path) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <Home
                          outBoundLeadsDetail={outBoundLeadsDetail}
                          setOutBoundLeadsDetail={setOutBoundLeadsDetail}
                          setUserDataAccess={setUserDataAccess}
                          setCurrentRoute={setCurrentRoute}
                          setIsUploadLead={setIsUploadLead}
                          isUploadLead={isUploadLead}
                          setIsTabClick={setIsTabClick}
                        />
                      }
                    />
                  ))}
                  <Route
                    path="/homepage"
                    element={<Homepage />}
                  />
                  <Route
                    path="/Dashboard"
                    element={
                      renderOrgProtectedRoute(
                        "dashboard",
                        true,
                        <Dashboard
                          setDashboardUrl={setDashboardUrl}
                          setSideBarLinkActive={setSideBarLinkActive}
                          userName={userName}
                          userProfession={userProfession}
                        />,
                      )
                    }
                  />
                  <Route
                    path="/AdminDashboard"
                    element={
                      // PracticeId is unset until /api/currentUser resolves, so this
                      // waits for the real role instead of judging by userType's
                      // transient "client" default and bouncing a real admin home.
                      !PracticeId ? <RouteLoading /> : userType === "admin" ? (
                        <AdminDashboard />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/agent-config"
                    element={renderOrgProtectedRoute("agentConfig", false, <AgentConfigPage />)}
                  />
                  <Route
                    path="/tools"
                    element={renderOrgProtectedRoute("toolDefinitions", false, <ToolDefinitionsPage />)}
                  />
                  <Route
                    path="/agent-releases"
                    element={renderOrgProtectedRoute("agentReleases", false, <AgentReleasesPage />)}
                  />
                  {isVoiceOpsEnabled && (
                    <>
                      <Route
                        path="/VoiceOps"
                        element={
                          !PracticeId ? <RouteLoading /> : renderOrgProtectedRoute("voiceOps", false, <VoiceOpsPage />)
                        }
                      />
                      <Route
                        path="/VoiceOps/:sessionId"
                        element={
                          !PracticeId ? <RouteLoading /> : renderOrgProtectedRoute("voiceOps", false, <VoiceOpsPage />)
                        }
                      />
                    </>
                  )}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </LayoutShell>
            ) : (
              // Preserve ?tab=summary only for Summary deep links, and
              // remember where the user actually was so a transient
              // rehydration race doesn't strand them on the login page
              // or bounce them back to a different page than they refreshed.
              <Navigate
                to={hasSummaryTab ? `/login/VerifM${summaryQuery}` : "/login"}
                state={{ from: `${location.pathname}${location.search}` }}
                replace
              />
            )
          }
        />

      </Routes >
    </>
  );
}

export default App;

